// apps/web/src/features/auth/components/LoginForm.tsx
// Spec §17 verbatim (with explicit setSession+navigate on success per plan
// Task 4.8.3). The `refreshToken` never reaches this component — useLogin's
// mutationFn strips it at the boundary. On 401 / 429 / other errors we route
// the mapping through api/errors.errorMessage() and surface via toast.
import { useForm, Controller } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { zodResolver } from '@hookform/resolvers/zod';
import { useNavigate } from 'react-router';
import { ZodError } from 'zod';
import {
  LoginInputSchema,
  MeResponseSchema,
  type LoginInput,
  type MeResponse,
} from '@magimp/shared';
import { useApiClient, useLogin } from '@magimp/shared-hooks';
import { Input, Button, FormRow } from '../../../ui/index.js';
import { useAuthStore } from '../../../stores/auth.js';
import { useToastStore } from '../../../stores/toast.js';
import { errorMessage } from '../../../api/errors.js';
import { landingForUser } from '../../../shell/menu.js';
import { PasswordField } from './PasswordField.js';

export function LoginForm() {
  const { t } = useTranslation('auth');
  const navigate = useNavigate();
  const showToast = useToastStore((s) => s.show);
  const login = useLogin();
  const client = useApiClient();
  const {
    control,
    handleSubmit,
    formState: { errors, isSubmitting },
  } = useForm<LoginInput>({
    resolver: zodResolver(LoginInputSchema),
    defaultValues: { email: '', password: '' },
  });
  const pending = isSubmitting || login.isPending;

  const onSubmit = (d: LoginInput) => {
    login.mutate(d, {
      onSuccess: async ({ accessToken, user }) => {
        const { permissions, ...rest } = user;
        // INVARIANT (#440 Phase 3): isOnShift is sourced exclusively
        //   from the top-level `MeResponse.isOnShift` slot —
        //   `UserSchema` carries no shift state. Seed the slot to
        //   `false` synchronously so the SPA can route immediately,
        //   then reconcile from the authoritative `/auth/me` payload
        //   below.
        useAuthStore.getState().setSession(accessToken, rest, permissions, false);
        navigate(landingForUser(rest, permissions), { replace: true });
        // Best-effort canonical reconcile. A network/parse failure here
        // leaves the slot at false; the next page reload re-hydrates
        // through `useAuthBootstrap` against the same endpoint, so a
        // mid-shift logist is at most one reload away from the correct
        // toggle state. Backend write-side (PATCH /me/shift) remains
        // the source of truth either way.
        const beforeShiftWrites = useAuthStore.getState().shiftWriteCount;
        // Capture the post-setSession sessionRevision. This is stable
        // until the next `clear()`; a logout + re-login (#443 race)
        // bumps it, so a stale /auth/me from THIS login that lands
        // after the user logs out and back in will fail the revision
        // check inside setUserShift even though the userId matches.
        const beforeSessionRevision = useAuthStore.getState().sessionRevision;
        try {
          const me = await client.get<MeResponse>('/auth/me', {}, MeResponseSchema);
          // INVARIANT (#439, #443 race-guard): drop the reconcile in
          //   three races —
          //   (a) cross-user: logout + re-login as a different user
          //       happened mid-flight (handled by `setUserShift`'s
          //       internal user-id check on `rest.id`).
          //   (b) same-session: a `setUserShift` write (toggle click +
          //       PATCH /me/shift response) landed mid-flight
          //       (`shiftWriteCount` advanced beyond the captured
          //       baseline). The newer interactive write is more
          //       authoritative than this background snapshot.
          //   (c) same-user re-login: logout + re-login as the SAME
          //       user happened mid-flight (`sessionRevision`
          //       advanced; handled by `setUserShift`'s internal
          //       revision check).
          if (useAuthStore.getState().shiftWriteCount === beforeShiftWrites) {
            useAuthStore.getState().setUserShift(rest.id, beforeSessionRevision, me.isOnShift);
          }
        } catch (e) {
          // INVARIANT (#440 Phase 3): a ZodError here is a real backend
          //   contract bug now that `MeResponseSchema.isOnShift` is REQUIRED
          //   (no default). The seeded `false` will stand until the next
          //   reload re-hydrates through `useAuthBootstrap` (which has its
          //   own retry/clear policy on persistent drift), so login itself
          //   stays usable — but the drift must be visible in the console
          //   so it doesn't masquerade as "logist always off shift".
          //   Network/ApiClient errors stay silent (best-effort reconcile).
          if (e instanceof ZodError) {
            console.error('[LoginForm] /auth/me response failed schema parse after login', e);
          }
        }
      },
      onError: (err) => {
        showToast({ tone: 'error', text: errorMessage(err, 'login') });
      },
    });
  };

  return (
    <form onSubmit={handleSubmit(onSubmit)} noValidate>
      <FormRow label="Email" error={errors.email?.message}>
        <Controller
          control={control}
          name="email"
          render={({ field }) => (
            <Input
              value={field.value ?? ''}
              onChange={(v) => field.onChange(v)}
              invalid={!!errors.email}
              icon="mail"
              type="email"
              autoComplete="username"
            />
          )}
        />
      </FormRow>
      <FormRow label={t('login.password')} error={errors.password?.message}>
        <Controller
          control={control}
          name="password"
          render={({ field }) => (
            <PasswordField
              value={field.value ?? ''}
              onChange={field.onChange}
              invalid={!!errors.password}
              autoComplete="current-password"
            />
          )}
        />
      </FormRow>
      {pending ? (
        <Button
          type="submit"
          disabled={true}
          icon="loader"
          kind="primary"
          style={{ width: '100%', justifyContent: 'center', marginTop: 'var(--s-3)' }}
        >
          {t('login.pending')}
        </Button>
      ) : (
        <Button
          type="submit"
          kind="primary"
          style={{ width: '100%', justifyContent: 'center', marginTop: 'var(--s-3)' }}
        >
          {t('login.submit')}
        </Button>
      )}
    </form>
  );
}
